import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Send,
  Sparkles,
  Bot,
  User,
  Trash2,
  ExternalLink,
  HelpCircle,
  CheckCircle2,
} from "lucide-react";
import { AssistantChatMessage, sendAssistantMessage } from "../../services/assistantService";

interface AssistantChatProps {
  isCompact?: boolean;
}

const STARTER_PROMPTS = [
  "How do I build a Proposal Response Plan in Stage 3?",
  "How do I shred an RFP document?",
  "How does the Plan Readiness Audit and Freeze Gate work?",
  "What does the Evaluator Red Team check?",
  "How do I assign section ownership and balance workload?",
  "How does Go/No-Go fit scoring work?",
  "How do I track RFP addenda and scope shifts?",
  "How do I export to Google Docs?",
  "How do I harvest answers back to the library?",
];

export const AssistantChat: React.FC<AssistantChatProps> = ({ isCompact = false }) => {
  const [messages, setMessages] = useState<AssistantChatMessage[]>([
    {
      id: "welcome-1",
      role: "assistant",
      content:
        "Hello! I am your **MLC Proposal HQ Assistant**. How can I help you today? You can ask me how any feature works, how to navigate workflows across all 6 stages, or click any of the suggested topics below.",
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      suggestedQuestions: STARTER_PROMPTS.slice(0, 5),
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    if (typeof messagesEndRef.current?.scrollIntoView === "function") {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSendMessage = useCallback(
    async (textToSend: string) => {
      const text = textToSend.trim();
      if (!text || isLoading) return;

      const userMsg: AssistantChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: text,
        timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      };

      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setError(null);
      setIsLoading(true);

      try {
        const result = await sendAssistantMessage(text, messages);

        const assistantMsg: AssistantChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: result.reply,
          timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          suggestedQuestions: result.suggestedQuestions,
          suggestedLinks: result.suggestedLinks,
        };

        setMessages((prev) => [...prev, assistantMsg]);
      } catch (err) {
        console.error("Assistant chat error:", err);
        setError("Failed to get a response. Please check your connection and try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, messages],
  );

  const handleClearChat = () => {
    setMessages([
      {
        id: `welcome-${Date.now()}`,
        role: "assistant",
        content:
          "Conversation cleared. What else would you like to know about **MLC Proposal HQ**?",
        timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        suggestedQuestions: STARTER_PROMPTS,
      },
    ]);
    setError(null);
  };

  // Helper to render simple markdown formatting
  const renderFormattedContent = (content: string) => {
    const lines = content.split("\n");

    const parseInline = (text: string) => {
      const parts = text.split(/(\*\*.*?\*\*|`.*?`)/g);
      return parts.map((part, pIdx) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return (
            <strong key={pIdx} className="font-semibold text-slate-900 dark:text-white">
              {part.slice(2, -2)}
            </strong>
          );
        }
        if (part.startsWith("`") && part.endsWith("`")) {
          return (
            <code
              key={pIdx}
              className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs text-slate-800 dark:bg-slate-800 dark:text-slate-200"
            >
              {part.slice(1, -1)}
            </code>
          );
        }
        return part;
      });
    };

    return (
      <div className="space-y-1.5 text-sm leading-relaxed">
        {lines.map((line, idx) => {
          const trimmed = line.trim();
          if (!trimmed) {
            return <div key={idx} className="h-1" />;
          }

          // Bullet item (- or *)
          if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
            const bulletBody = trimmed.replace(/^[-*]\s+/, "");
            return (
              <div key={idx} className="flex items-start gap-2 pl-2">
                <span className="text-[#2ba5b5] font-bold">•</span>
                <span className="flex-1">{parseInline(bulletBody)}</span>
              </div>
            );
          }

          // Numbered item (e.g. 1. , 2. )
          const matchNum = trimmed.match(/^(\d+)\.\s+(.*)/);
          if (matchNum) {
            return (
              <div key={idx} className="flex items-start gap-2 pl-2">
                <span className="font-semibold text-[#2ba5b5]">{matchNum[1]}.</span>
                <span className="flex-1">{parseInline(matchNum[2] || "")}</span>
              </div>
            );
          }

          // Headings (### , ## , # )
          const matchHeading = trimmed.match(/^(#{1,3})\s+(.*)/);
          if (matchHeading && matchHeading[1] && matchHeading[2]) {
            const level = matchHeading[1].length;
            const headingText = matchHeading[2];
            const sizeClass =
              level === 1
                ? "text-base font-bold text-slate-900 dark:text-white mt-2"
                : level === 2
                  ? "text-sm font-bold text-slate-900 dark:text-white mt-1.5"
                  : "text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mt-1";
            return (
              <div key={idx} className={sizeClass}>
                {parseInline(headingText)}
              </div>
            );
          }

          return <p key={idx}>{parseInline(line)}</p>;
        })}
      </div>
    );
  };

  return (
    <div
      className={`flex flex-col rounded-xl border border-slate-200/80 bg-white/80 shadow-xs dark:border-slate-800/80 dark:bg-slate-900/80 ${
        isCompact ? "h-[500px]" : "h-[620px]"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200/80 px-4 py-3 dark:border-slate-800/80">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-500/10 text-[#2ba5b5] dark:bg-teal-400/10">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Proposal HQ Assistant
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Interactive platform guide & workflow expert
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleClearChat}
          title="Clear conversation"
          className="btn-animate flex items-center gap-1 rounded-md px-2 py-1 text-xs text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          <Trash2 className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Clear</span>
        </button>
      </div>

      {/* Message List */}
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex items-start gap-3 ${
              msg.role === "user" ? "flex-row-reverse" : "flex-row"
            }`}
          >
            {/* Avatar */}
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                msg.role === "user"
                  ? "bg-slate-700 text-white dark:bg-slate-600"
                  : "bg-[#2ba5b5] text-white"
              }`}
            >
              {msg.role === "user" ? <User className="h-3.5 w-3.5" /> : <Bot className="h-4 w-4" />}
            </div>

            {/* Bubble */}
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-2xs ${
                msg.role === "user"
                  ? "bg-[#2ba5b5] text-white rounded-tr-xs"
                  : "glass border border-slate-200/80 bg-white/95 text-slate-800 dark:border-slate-800/80 dark:bg-slate-800/90 dark:text-slate-200 rounded-tl-xs"
              }`}
            >
              {renderFormattedContent(msg.content)}

              {/* Suggested navigation links */}
              {msg.suggestedLinks && msg.suggestedLinks.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2.5 dark:border-slate-700">
                  {msg.suggestedLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="btn-animate inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-[#2ba5b5] hover:bg-teal-100 dark:bg-teal-950/60 dark:text-teal-300 dark:hover:bg-teal-900/60"
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      <span>{link.label}</span>
                      <ExternalLink className="h-2.5 w-2.5 opacity-60" />
                    </Link>
                  ))}
                </div>
              )}

              {/* Timestamp */}
              <div
                className={`mt-1.5 text-[10px] ${
                  msg.role === "user" ? "text-teal-100 text-right" : "text-slate-400"
                }`}
              >
                {msg.timestamp}
              </div>
            </div>
          </div>
        ))}

        {/* Loading Indicator */}
        {isLoading && (
          <div className="flex items-center gap-3">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#2ba5b5] text-white">
              <Bot className="h-4 w-4" />
            </div>
            <div className="glass flex items-center gap-1.5 rounded-2xl rounded-tl-xs border border-slate-200/80 bg-white/95 px-4 py-3 dark:border-slate-800/80 dark:bg-slate-800/90">
              <span className="h-2 w-2 animate-bounce rounded-full bg-[#2ba5b5]" />
              <span className="h-2 w-2 animate-bounce rounded-full bg-[#2ba5b5] [animation-delay:0.2s]" />
              <span className="h-2 w-2 animate-bounce rounded-full bg-[#2ba5b5] [animation-delay:0.4s]" />
              <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                Consulting Proposal HQ knowledge...
              </span>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Starter Prompts Bar */}
      <div className="border-t border-slate-200/60 bg-slate-50/50 p-2.5 dark:border-slate-800/60 dark:bg-slate-900/50">
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs scrollbar-thin">
          <HelpCircle className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className="shrink-0 text-[11px] font-medium text-slate-500 dark:text-slate-400">
            Suggested:
          </span>
          {STARTER_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => handleSendMessage(prompt)}
              disabled={isLoading}
              className="btn-animate shrink-0 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] text-slate-700 hover:border-teal-300 hover:bg-teal-50 hover:text-[#2ba5b5] disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-teal-700 dark:hover:bg-slate-700"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>

      {/* Input Bar */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSendMessage(input);
        }}
        className="flex items-center gap-2 border-t border-slate-200/80 p-3 dark:border-slate-800/80"
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask anything about MLC Proposal HQ..."
          disabled={isLoading}
          className="flex-1 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2ba5b5] focus:outline-hidden focus:ring-1 focus:ring-[#2ba5b5] disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        />

        <button
          type="submit"
          disabled={!input.trim() || isLoading}
          className="btn-animate flex h-9 w-9 items-center justify-center rounded-lg bg-[#2ba5b5] text-white shadow-xs hover:bg-[#238997] disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
};
